import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/company/type/")({
  loader: async () => {
    const { COMPANY_ENTITY_TYPES } = await import("@/content/company-corpus/company-types");
    return { items: COMPANY_ENTITY_TYPES.map((x) => ({ slug: x.slug, title: x.titleTh, desc: x.whoTh })) };
  },
  component: CompanyTypeHub,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `จดทะเบียนบริษัทในไทย ${loaderData?.items.length ?? 0} รูปแบบนิติบุคคล — เงื่อนไขและขั้นตอน`,
        description:
          "เปรียบเทียบรูปแบบนิติบุคคลสำหรับลงทุนในไทย: บริษัทจำกัดไทยถือหุ้นข้างมาก ใบอนุญาตประกอบธุรกิจของคนต่างด้าว สนธิสัญญาไมตรีไทย–สหรัฐฯ BOI สาขา สำนักงานผู้แทน และ IBC พร้อมเงื่อนไขทุน เอกสาร และแหล่งอ้างอิงราชการ",
        path: "/company/type",
        alternates: [{ lang: "en", path: "/en/company/type" }],
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/company/type")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "รูปแบบนิติบุคคล", url: `${site}/company/type` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: (loaderData?.items ?? []).map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.title,
              url: `${site}/company/type/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

function CompanyTypeHub() {
  const { items } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>รูปแบบนิติบุคคล</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          รูปแบบนิติบุคคลสำหรับประกอบธุรกิจในไทย — {items.length} ทางเลือก
        </h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          แต่ละหน้าอธิบายว่าใครใช้รูปแบบนี้ได้ เงื่อนไขทุนและสัดส่วนผู้ถือหุ้น เอกสารที่ต้องเตรียม ลำดับขั้นตอน
          หน่วยงานผู้พิจารณา และข้อผิดพลาดที่ทำให้เรื่องถูกตีกลับ ข้อมูลอ้างอิงประมวลกฎหมายแพ่งและพาณิชย์
          พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 และประกาศของกรมพัฒนาธุรกิจการค้า กรมสรรพากร และ BOI
          การอนุมัติใบอนุญาตหรือสิทธิประโยชน์ทุกกรณีเป็นดุลพินิจของหน่วยงาน
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((t: { slug: string; title: string; desc: string }) => (
            <a key={t.slug} href={`/company/type/${t.slug}`} className="rounded-lg border bg-card p-5 transition hover:border-gold">
              <h2 className="mb-2 text-lg font-semibold">{t.title}</h2>
              <p className="text-sm text-muted-foreground">{t.desc}</p>
            </a>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-xl font-bold">ไม่แน่ใจว่าควรใช้รูปแบบใด?</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งกิจกรรมธุรกิจ สัญชาติผู้ถือหุ้น และแผนการจ้างงานให้ทีมงานทางโทรศัพท์ LINE หรืออีเมล
            เราจะช่วยเทียบเงื่อนไขของแต่ละรูปแบบให้ก่อนตัดสินใจ เราไม่รับจัดโครงสร้างผู้ถือหุ้นนอมินี
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <p className="mt-8 text-xs text-muted-foreground">
          ข้อมูล ณ สิงหาคม 2569 (2026) โปรดตรวจสอบเงื่อนไขและค่าธรรมเนียมล่าสุดกับกรมพัฒนาธุรกิจการค้าก่อนดำเนินการ
        </p>
        <p className="mt-4 text-sm">
          อ่านคำถามที่พบบ่อย: <a href="/company/faq" className="underline hover:text-gold">FAQ จดทะเบียนบริษัท</a> ·{" "}
          <a href="/work-permit/type" className="underline hover:text-gold">ใบอนุญาตทำงาน</a>
        </p>
      </main>
      <SiteFooter />
    </>
  );
}